import React from "react";
import { motion } from "framer-motion";
import { Leaf, Sparkles, Flame } from "lucide-react";

const STATS = [
  { k: "3", v: "Generations" },
  { k: "12+", v: "Local Ingredients" },
  { k: "0", v: "Preservatives" },
];

const NOTES = [
  {
    icon: Leaf,
    title: "Farm-fresh sourcing",
    text: "Tomatoes, amla and curry leaves picked from Karnataka farms every week.",
  },
  {
    icon: Flame,
    title: "Slow-roasted spices",
    text: "Byadgi chilies and whole spices roasted on a low flame — never machine-blitzed.",
  },
  {
    icon: Sparkles,
    title: "Bottled fresh",
    text: "Made in small batches and sealed the same day. Nothing sits on a shelf.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      className="py-20 md:py-32 bg-[#fdf3f0] relative overflow-hidden"
      data-testid="about-section"
    >
      <div className="pointer-events-none absolute -top-16 -right-16 w-96 h-96 rounded-full bg-[#993311]/5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 w-96 h-96 rounded-full bg-[#2F4F2F]/5 blur-3xl" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-hand text-2xl text-[#993311]"
        >
          — our story
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="mt-3 font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-stone-900"
        >
          Real chutney,{" "}
          <span className="italic hand-underline">the village way.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-8 text-stone-600 md:text-lg leading-relaxed max-w-2xl mx-auto"
        >
          Halli Chutney is made for people who love the real taste of traditional
          chutneys. Inspired by Karnataka&apos;s village kitchens, every jar is
          packed with authentic flavors, spices, and homemade goodness — the way{" "}
          <span className="font-hand text-2xl text-[#993311]">ajji</span> used to
          make them.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-12 grid grid-cols-3 gap-3 md:gap-8 max-w-2xl mx-auto"
        >
          {STATS.map((s) => (
            <div
              key={s.v}
              className="border-t-2 border-[#993311] pt-4"
              data-testid={`about-stat-${s.v.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="font-serif text-3xl md:text-5xl text-stone-900">
                {s.k}
              </div>
              <div className="text-[10px] md:text-xs uppercase tracking-widest text-stone-500 mt-1.5">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="mt-16 md:mt-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {NOTES.map((n, i) => {
            const Icon = n.icon;
            return (
              <motion.div
                key={n.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white border border-stone-200/70 rounded-2xl p-6 text-left"
                data-testid={`about-note-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#993311]/10 text-[#993311] grid place-items-center">
                  <Icon className="w-4 h-4" />
                </div>
                <h3 className="mt-4 font-serif text-lg text-stone-900">
                  {n.title}
                </h3>
                <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">
                  {n.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
